import type { BusinessCreate } from 'src/features/business/shared/schemas/business'
import BusinessCreateEmail from 'src/features/business-create/views/form-steps/email'
import BusinessCreateFirstName from 'src/features/business-create/views/form-steps/first-name'
import BusinessCreateLastName from 'src/features/business-create/views/form-steps/last-name'
import BusinessCreateLocationTypeStep from 'src/features/business-create/views/form-steps/location-type'
import BusinessSelectLocationStep from 'src/features/business-create/views/form-steps/map'
import BusinessCreatePhoneNumber from 'src/features/business-create/views/form-steps/phone-number'
import BusinessCreatePurpose from 'src/features/business-create/views/form-steps/purpose'

import { HouseIcon } from '../icons/house-icon'
import { InfoIcon } from '../icons/info-icon'
import { LynkIcon } from '../icons/lynk-icon'
import { PinIcon } from '../icons/pin-icon'
import { UserIcon } from '../icons/user-icon'
import BusinessCreateAdditionalInfoStep from '../views/form-steps/additional-info'
import BusinessCreateClosedDateStep from '../views/form-steps/closed-date'
import BusinessCreateIsClosedStep from '../views/form-steps/is-closed'
import BusinessCreateWorkflowsStep from '../views/form-steps/workflows'

export type FormKeys = keyof BusinessCreate

export type StepItem = {
  component: React.ReactNode
  requiredFormData?: FormKeys[]
  progress: {
    block: number
    step: number
  }
}

export type BusinessCreateConfig = {
  totalSteps: number
  steps: Partial<Record<BCStepName, StepItem>>
}

export type BCStepName =
  | 'first-name'
  | 'last-name'
  | 'phone-number'
  | 'email'
  | 'purpose'
  | 'is-closed'
  | 'closed-date'
  | 'location-type'
  | 'select-location'
  | 'additional-info'
  | 'workflows'

const BASE_STEPS: Partial<Record<BCStepName, StepItem>> = {
  'phone-number': {
    component: <BusinessCreatePhoneNumber />,
    requiredFormData: ['consumerPhone'],
    progress: {
      block: 0,
      step: 0,
    },
  },
  'is-closed': {
    component: <BusinessCreateIsClosedStep />,
    requiredFormData: ['isClosed'],
    progress: {
      block: 0,
      step: 1,
    },
  },
  'closed-date': {
    component: <BusinessCreateClosedDateStep />,
    requiredFormData: ['closedAt'],
    progress: {
      block: 0,
      step: 2,
    },
  },
  'first-name': {
    component: <BusinessCreateFirstName />,
    requiredFormData: [],
    progress: {
      block: 0,
      step: 3,
    },
  },
  'last-name': {
    component: <BusinessCreateLastName />,
    requiredFormData: [],
    progress: {
      block: 0,
      step: 4,
    },
  },
  email: {
    component: <BusinessCreateEmail />,
    requiredFormData: [],
    progress: {
      block: 0,
      step: 5,
    },
  },
  purpose: {
    component: <BusinessCreatePurpose />,
    requiredFormData: [],
    progress: {
      block: 1,
      step: 0,
    },
  },
  'location-type': {
    component: <BusinessCreateLocationTypeStep />,
    progress: {
      block: 2,
      step: 0,
    },
  },
  'select-location': {
    component: <BusinessSelectLocationStep />,
    progress: {
      block: 2,
      step: 1,
    },
  },
  'additional-info': {
    component: <BusinessCreateAdditionalInfoStep />,
    progress: {
      block: 3,
      step: 0,
    },
  },
}

export const BusinessCreateConfig: BusinessCreateConfig = {
  totalSteps: Object.keys(BASE_STEPS).length,
  steps: {
    ...BASE_STEPS,
  },
}

export const BusinessCreateConfigWithWorkflows: BusinessCreateConfig = {
  totalSteps: Object.keys(BASE_STEPS).length + 1,
  steps: {
    ...BASE_STEPS,
    workflows: {
      component: <BusinessCreateWorkflowsStep />,
      progress: {
        block: 4,
        step: 0,
      },
    },
  },
}

export type Block = {
  icon: React.ReactNode
  key: string
  title: string
  blockNumber: number
  blockSteps: number
}

export const BASE_BLOCKS: Block[] = [
  {
    icon: <UserIcon />,
    key: 'personal-info',
    title: 'Personal Info',
    blockNumber: 0,
    blockSteps: 6,
  },
  {
    icon: <HouseIcon />,
    key: 'interest',
    title: 'Interests',
    blockNumber: 1,
    blockSteps: 1,
  },
  {
    icon: <PinIcon />,
    key: 'location',
    title: 'Location',
    blockNumber: 2,
    blockSteps: 2,
  },
  {
    icon: <InfoIcon size={22} />,
    key: 'additional-info',
    title: 'Additional Info',
    blockNumber: 3,
    blockSteps: 1,
  },
]

export const SUBSCRIBED_BLOCKS: Block[] = [
  ...BASE_BLOCKS,
  {
    icon: <LynkIcon size={22} />,
    key: 'workflows',
    title: 'Workflows',
    blockNumber: 4,
    blockSteps: 1,
  },
]
